import React, { useRef, useEffect } from 'react';
import * as THREE from 'three';
import useIsMobile from '../../hooks/useIsMobile';

/** FAQ page — floating 3D question marks at different depths.
 *  Each one rotates and bobs gently; depth parallax simulated via blur + scale.
 *  Uses three-mesh-style 2D Plane with QuestionMark texture for performance.
 */

function makeQuestionTexture(colour) {
    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 256;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, 256, 256);
    ctx.fillStyle = colour;
    ctx.font = 'bold 220px "Plus Jakarta Sans", sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('?', 128, 134);
    const tex = new THREE.CanvasTexture(canvas);
    tex.anisotropy = 4;
    return tex;
}

const COLOURS = ['#7B61FF', '#00E5CC', '#FF3D8A', '#FF8A3D', '#FFD700'];
const COUNT = 18;

const FloatingQuestionsBg = () => {
    const mountRef = useRef(null);
    const isMobile = useIsMobile();

    useEffect(() => {
        if (isMobile) return;
        const mount = mountRef.current;
        if (!mount) return;
        const w = mount.clientWidth, h = mount.clientHeight;

        const scene = new THREE.Scene();
        const camera = new THREE.PerspectiveCamera(55, w / h, 0.1, 100);
        camera.position.z = 12;

        const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        renderer.setSize(w, h);
        renderer.setClearColor(0x000000, 0);
        mount.appendChild(renderer.domElement);

        // Pre-build textures per colour
        const textures = COLOURS.map(makeQuestionTexture);

        const marks = [];
        for (let i = 0; i < COUNT; i++) {
            const ci = i % COLOURS.length;
            const tex = textures[ci];
            const mat = new THREE.SpriteMaterial({
                map: tex,
                transparent: true,
                opacity: 0.65,
            });
            const sp = new THREE.Sprite(mat);
            const depth = (Math.random() - 0.5) * 8; // z
            const scale = 0.9 + (4 + depth) * 0.3; // closer => bigger
            sp.scale.setScalar(scale);
            sp.position.set(
                (Math.random() - 0.5) * 18,
                (Math.random() - 0.5) * 11,
                depth
            );
            sp.userData = {
                baseY: sp.position.y,
                bobAmp: 0.4 + Math.random() * 0.6,
                bobSpeed: 0.3 + Math.random() * 0.4,
                phase: Math.random() * Math.PI * 2,
                spin: (Math.random() - 0.5) * 0.4,
            };
            marks.push(sp);
            scene.add(sp);
        }

        // Mouse parallax
        const mouse = { x: 0, y: 0, tx: 0, ty: 0 };
        const onMouseMove = (e) => {
            const rect = mount.getBoundingClientRect();
            mouse.tx = (e.clientX - rect.left) / rect.width - 0.5;
            mouse.ty = (e.clientY - rect.top) / rect.height - 0.5;
        };
        window.addEventListener('mousemove', onMouseMove);

        const onResize = () => {
            const W = mount.clientWidth, H = mount.clientHeight;
            renderer.setSize(W, H);
            camera.aspect = W / H;
            camera.updateProjectionMatrix();
        };
        window.addEventListener('resize', onResize);

        const clock = new THREE.Clock();
        let raf = 0;
        const tick = () => {
            raf = requestAnimationFrame(tick);
            const t = clock.getElapsedTime();
            mouse.x += (mouse.tx - mouse.x) * 0.05;
            mouse.y += (mouse.ty - mouse.y) * 0.05;

            marks.forEach((m) => {
                const d = m.userData;
                m.position.y = d.baseY + Math.sin(t * d.bobSpeed + d.phase) * d.bobAmp;
                m.material.rotation += d.spin * 0.01;
            });
            camera.position.x = mouse.x * 1.8;
            camera.position.y = -mouse.y * 1.2;
            camera.lookAt(0, 0, 0);

            renderer.render(scene, camera);
        };
        tick();

        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('resize', onResize);
            if (mount && renderer.domElement.parentNode === mount) {
                mount.removeChild(renderer.domElement);
            }
            marks.forEach((m) => m.material.dispose());
            textures.forEach((t) => t.dispose());
            renderer.dispose();
        };
    }, [isMobile]);

    if (isMobile) {
        return (
            <div
                className="absolute inset-0 pointer-events-none"
                style={{
                    background:
                        'radial-gradient(circle at 20% 30%, rgba(37,99,235,0.10), transparent 50%), radial-gradient(circle at 80% 30%, rgba(56,189,248,0.10), transparent 50%), radial-gradient(circle at 50% 80%, rgba(14,165,233,0.08), transparent 55%)',
                }}
                aria-hidden="true"
            />
        );
    }

    return (
        <div
            ref={mountRef}
            className="absolute inset-0 w-full h-full pointer-events-none"
            aria-hidden="true"
            data-testid="bg-floating-questions"
        />
    );
};

export default FloatingQuestionsBg;
